import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  Alert,
  Box,
  List,
  ListItemText,
  ListItem as MUIListItem,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useAppSelector } from '../../../../../redux/store.model';
import {
  getAllGroupsInWorkspaceWithChainGroups,
  getAllGroupsWithChainGroups,
} from '../../../../../redux/groups/selectors/searchSelectors';
import { getMyWorkspaceSelector } from '../../../../../redux/stoerkID/selectors/StoerkId.selectors';
import { isSCCManager } from '../../../redux/rights/selectors/roles';
import { useNavigate, useParams } from 'react-router-dom';
import { getPolyglot } from '../../../../../i18n';
import { BusinessRole } from '../../../redux/businessModel/model/model';
import { ListItem } from '../../../../../theme/components';
import GroupModel from '../../../../../redux/groups/api/GroupModel';
import { isCompany, isStore } from '../../../util/invitationsUtilFunctions';
import { CompanyIcon, StoreIcon } from '../../../theme/icons';
import { NormalizedGroup } from '../../../../../redux/groups/api/GroupAdapter';

interface ListEntitiesSearchProps {
  searchValue?: string;
  close?: () => void;
}

function GroupsSearch(props: Readonly<ListEntitiesSearchProps>) {
  const { searchValue, close } = props;
  const params = useParams<{ groupId: string }>();
  const groups = useAppSelector(getAllGroupsWithChainGroups);
  const workspaceId = useAppSelector(getMyWorkspaceSelector)?.id ?? '';
  const groupsInWorkspace = useAppSelector((state) =>
    getAllGroupsInWorkspaceWithChainGroups(state, { workspaceId })
  );
  const isAdmin = useAppSelector(isSCCManager);

  const navigate = useNavigate();

  const searchGroups = isAdmin ? [...groupsInWorkspace] : groups;

  const filterGroupByStoreParent = (
    group: NormalizedGroup,
    isAdmin: boolean
  ) => {
    return (
      isStore(group as NormalizedGroup) &&
      (group.parentGroupId === params.groupId || isAdmin)
    );
  };

  const groupsResults = useMemo(
    () =>
      searchGroups.filter(
        (group) =>
          group.name &&
          searchValue?.length &&
          group.name.toLowerCase().includes(searchValue?.toLowerCase()) &&
          ((isAdmin && isCompany(group as NormalizedGroup)) ||
            filterGroupByStoreParent(group as NormalizedGroup, isAdmin))
      ),
    [groups, searchValue, groupsInWorkspace, isAdmin]
  );

  const groupsResultsLimited = useMemo(
    () => groupsResults.slice(0, 100),
    [groupsResults]
  );
  const nGroupsLimited = groupsResults.length - groupsResultsLimited.length;

  const polyglot = getPolyglot();

  const onClick = (group: any) => () => {
    onNavigateToMerchandiser(group);

    close?.();
  };

  const onNavigateToMerchandiser = (group: any) => {
    if (group?.attributes?.['BUSINESS_ROLE'] === BusinessRole.STORE) {
      const groupId = group.id;
      navigate(`/merchandiser/${groupId}`);
    } else if (group?.attributes?.['BUSINESS_ROLE'] === BusinessRole.COMPANY) {
      navigate(`/companies/group/${group?.id}`);
    } else {
      navigate(`/storelist/group/${group?.id}`);
    }
  };

  return (
    <Accordion defaultExpanded={true}>
      <AccordionSummary
        id="accordion-search-groups-title"
        aria-controls="accordion-search-groups-title"
        expandIcon={<ExpandMoreIcon />}
      >
        <Typography variant="h5">
          {polyglot.t('group.results_title')} ({groupsResults.length})
        </Typography>
      </AccordionSummary>
      {groupsResultsLimited.length ? (
        <List>
          {groupsResultsLimited.map((group) => (
            <ListItem
              key={group.id}
              name={group?.name}
              iconURL={group?.iconURL}
              iconNode={
                <Box width="2rem" color="primary.main">
                  {isCompany(group as NormalizedGroup) ? (
                    <CompanyIcon />
                  ) : (
                    <StoreIcon />
                  )}
                </Box>
              }
              className={GroupModel.getClassesNamesFromDevice(group as any)}
              onClick={group && onClick(group)}
              ItemTextContainerProps={{
                sx: (theme) => ({
                  maxWidth: 'initial',
                  textAlign: 'left',
                  [theme.breakpoints.down('md')]: {
                    maxWidth: 'initial',
                  },
                }),
              }}
              TitleProps={{
                sx: (theme) => ({
                  maxWidth: '500px',
                  [theme.breakpoints.down('md')]: {
                    maxWidth: '200px',
                  },
                }),
              }}
            />
          ))}
          {groupsResults.length > 100 && (
            <MUIListItem>
              <ListItemText
                primary={`${nGroupsLimited} ${polyglot.t(
                  'device.search.message_results_limited'
                )}`}
              />
            </MUIListItem>
          )}
        </List>
      ) : (
        <Alert severity="info">
          {polyglot.t('device.search.message_no_results_found')}
        </Alert>
      )}
    </Accordion>
  );
}

export default React.memo(GroupsSearch);
